import React, {useEffect, useState} from 'react';
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  FormText,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from 'reactstrap';
import {IMMamaSettings} from 'app/shared/model/mmama-settings-model';
import {fromCents, toCents, toPercent, validateCentsString} from 'app/shared/util/money-utils';

interface IOverrideModalProps {
  opened: boolean;
  toggle: () => void;
  defaultAmount: number;
  paymentRequestedPrice: number;
  onSave: (number, isRecalculated) => void;
  settings: IMMamaSettings;
  calculatedCost?: number; // Prop for recalculated cost
}

const OverridePriceModal = ({
                              opened,
                              toggle,
                              onSave,
                              defaultAmount,
                              paymentRequestedPrice,
                              settings,
                              calculatedCost
                            }: IOverrideModalProps) => {

  const [amount, setAmount] = useState<number>(calculatedCost || defaultAmount);
  const [amountValid, setAmountValid] = useState(true);
  const [shouldSave, setShouldSave] = useState(false);
  const [saveAmount, setSaveAmount] = useState<number>(0);
  const [isCostRecalculated, setIsCostRecalculated] = useState<boolean>(false);

  const calculateMaxAmount = (): number => {
    // defaultAmount was already converted from cents e.g. 12301 -> 123.01; set back to 12301
    const amountInCents = toCents(paymentRequestedPrice);
    const maxAmount = Math.floor(amountInCents + (amountInCents * toPercent(settings.paymentEditThreshold)));
    return fromCents(maxAmount);
  }

  // Update amount when calculatedCost changes
  useEffect(() => {
    if (calculatedCost) {
      setAmount(calculatedCost);
      // Skip max amount validation for recalculated costs from Mapbox
      const isValid = validateCentsString(calculatedCost?.toString()) &&
        calculatedCost > 0;
      setAmountValid(isValid);
    }
  }, [calculatedCost]);

  // Reset amount to defaultAmount when modal opens
  useEffect(() => {
    if (opened) {
      const resetValue = calculatedCost || defaultAmount;
      setAmount(resetValue);
      // Validate the reset value - skip max amount check for calculated costs
      const isValid = validateCentsString(resetValue?.toString()) &&
        resetValue > 0 &&
        (calculatedCost ? true : resetValue <= calculateMaxAmount());
      setAmountValid(isValid);
      setIsCostRecalculated(!!calculatedCost);
    }
  }, [opened, defaultAmount, calculatedCost]);

  const onAmountChange = (e) => {
    const {target} = e;
    setAmount(target.value);
    // Skip max amount validation if this is a recalculated cost from Mapbox
    const isValid = validateCentsString(target.value) &&
      target.value > 0 &&
      (calculatedCost ? true : target.value <= calculateMaxAmount());
    setAmountValid(isValid);
    console.log("amount valid: {}", (amount > 0));
    console.log("amount less than max: {}", (amount < calculateMaxAmount()));
  }

  const onSubmit = () => {
    setSaveAmount(amount);
    setShouldSave(true);
    toggle();
  }

  const handleModalClosed = () => {
    if (shouldSave) {
      onSave(saveAmount, isCostRecalculated);
      setShouldSave(false);
    }
  };

  return (
    <Modal isOpen={opened} toggle={toggle} onClosed={handleModalClosed}>
      <ModalHeader toggle={toggle}>{calculatedCost ? 'Recalculated Amount' : 'New Amount'}</ModalHeader>
      <ModalBody>
        <Form>
          <FormGroup>
            <Label for="new-amount">New Amount</Label>
            <Input
              onChange={onAmountChange}
              value={amount}
              name="number"
              type="number"
              min={0}
              max={calculateMaxAmount()}
              id="new-amount"
              valid={amountValid}
              invalid={!amountValid}
              required
              disabled={!!calculatedCost}
            />
            {!calculatedCost && (
              <>
                <FormFeedback tooltip>New Amount must be greater than 0 and lower than max amount</FormFeedback>
                <FormText>Max amount: {calculateMaxAmount().toFixed(2)} (Original Requested
                  Amount: {paymentRequestedPrice.toFixed(2)})</FormText>
              </>
            )}
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="danger" onClick={toggle}>Cancel</Button>{' '}
        <Button color="primary" onClick={onSubmit} disabled={!amountValid}>{calculatedCost ? 'Update' : 'Edit'}</Button>
      </ModalFooter>
    </Modal>
  );
}

export default OverridePriceModal;
